<template>
  <FrequentlyAskedQuestions
    :class="cn('min-md:px-0 md:max-w-large container', props.class)"
    :classes="{ root: 'w-full' }"
  >
    <template #accordion="{ classes }">
      <UiAccordion :class="classes.container" type="single" collapsible>
        <UiAccordionItem :class="classes.question" value="what-is-finq">
          <UiAccordionTrigger :class="classes.trigger">What is FINQ?</UiAccordionTrigger>

          <UiAccordionContent :class="classes.content">
            <p>
              FINQ is the first AI-driven fund manager designed to bring a new level of objectivity and
              technology to investment strategy. We use data science to continuously analyze and rank
              financial assets, offering investors access to AI-powered ETFs built on systematic, continuos
              insights developed to pursue long-term performance.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <UiAccordionItem :class="classes.question" value="how-is-finq-different">
          <UiAccordionTrigger :class="classes.trigger"
            >How is FINQ different from other investment firms?</UiAccordionTrigger
          >

          <UiAccordionContent :class="classes.content">
            <p>
              Unlike traditional fund managers, FINQ relies entirely on AI-driven technology to manage
              investment strategies and ETFs. Our adaptive methodology continuously analyzes market data to
              deliver ETFs designed to challenge any index and pursue long-term performance — free from human
              bias and outdated methods.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <UiAccordionItem :class="classes.question" value="who-is-behind">
          <UiAccordionTrigger :class="classes.trigger">Who is behind FINQ?</UiAccordionTrigger>

          <UiAccordionContent :class="classes.content">
            <p>
              FINQ was founded by Eldad Tamir, founder of the Tamir Fishman Group, and Nir Zuk, the
              entrepreneur behind Palo Alto Networks, a company in the S&P 500. FINQ is an SEC-registered
              investment advisor operating under U.S. regulatory frameworks, and works with leading global
              legal and accounting firms to ensure transparency and compliance.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <!-- How does FINQ use AI to help investors? -->
        <UiAccordionItem :class="classes.question" value="how-does-finq-use-ai">
          <UiAccordionTrigger :class="classes.trigger"
            >How does FINQ use AI to help investors?</UiAccordionTrigger
          >

          <UiAccordionContent :class="classes.content">
            <p>
              FINQ’s proprietary AI technology continuously processes large volumes of market data to generate
              dynamic asset rankings. These insights directly inform the composition of our ETFs, allowing us
              to build strategies that respond to changing market conditions with speed and consistency. While
              the AI aims to support better decision-making, performance is not guaranteed.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <!-- What kind of information does FINQ provide for free, and why? -->

        <UiAccordionItem
          :class="classes.question"
          value="what-kind-of-information-does-finq-provide-for-free"
        >
          <UiAccordionTrigger :class="classes.trigger">
            What kind of information does FINQ provide for free, and why?
          </UiAccordionTrigger>

          <UiAccordionContent :class="classes.content">
            <p>
              We believe access to objective, data-driven market insights should be available to all
              investors. That’s why FINQ provides free AI-generated rankings of the top 500 U.S. stocks —
              designed to increase transparency and help investors make more informed decisions. These
              rankings are for informational purposes only and are not personalized recommendations.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <!-- Is FINQ suitable for institutional investors and financial advisors? -->

        <UiAccordionItem
          :class="classes.question"
          value="is-finq-suitable-for-institutional-investors-and-financial-advisors"
        >
          <UiAccordionTrigger :class="classes.trigger"
            >Is FINQ suitable for institutional investors and financial advisors?</UiAccordionTrigger
          >

          <UiAccordionContent :class="classes.content">
            <p>
              Yes. FINQ’s AI-powered ETFs are designed to support the needs of asset managers, financial
              advisors, and RIAs looking for innovative, scalable strategies. Our data-first approach offers a
              differentiated way to construct portfolios — though, like all investment products, FINQ’s ETFs
              involve risk and should be evaluated accordingly.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>

        <!-- How can I start using FINQ’s ETFs? -->

        <UiAccordionItem :class="classes.question" value="how-can-i-start-using-finqs-etfs">
          <UiAccordionTrigger :class="classes.trigger">
            How can I start using FINQ’s ETFs?</UiAccordionTrigger
          >

          <UiAccordionContent :class="classes.content">
            <p>
              FINQ’s ETFs are launching soon. You can explore them on our dedicated ETFs page and subscribe to
              receive updates. Once launched, they’ll be available through standard investment platforms.
              Before investing, we encourage you to review the ETF details, strategy information, and risk
              disclosures.
            </p>
          </UiAccordionContent>
        </UiAccordionItem>
      </UiAccordion>
    </template>
  </FrequentlyAskedQuestions>
</template>

<script setup lang="ts">
const props = defineProps<{
  class?: ClassValue
}>()

const { locale } = useI18n()
</script>

<style lang="scss" scoped>
:deep(p + p) {
  @apply mt-3;
}

:deep(b) {
  @apply font-medium;
}
</style>

<i18n lang="json">
{
  "he": {},
  "en": {}
}
</i18n>
